import { Button, ButtonAsLink } from '@cluey/cluey-components';
import { zodResolver } from '@hookform/resolvers/zod';
import isEmpty from 'lodash/isEmpty';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';

import { api } from '../../../../../api';
import type { CreateStudentParams } from '../../../../../api/services/student';
import { ShadowContainer } from '../../../../../components/ShadowContainer';
import { SpecialNeeds } from '../../../../../components/hub/student/SpecialNeeds';
import { ArrowBackIcon, ChevronDownIcon } from '../../../../../icons';
import { FormFlowProps } from '../../../../../types/hubInterfaces';
import { convertHubToSfLearningNeeds } from '../../../../../util/learning-needs';
import { PATH_HUB_CR_ADD_ENROLMENT, PATH_HUB_HOME } from '../../../../../util/pagePath';
import { usePageHeader } from '../../../../../v2/app-shell/usePageHeader';
import { ClueyPlusSpinner } from '../../../../../v2/common/ClueyPlusLoader';
import { InlineLoader } from '../../../auth2/common';
import { StudentProfileData } from '../../StudentProfile/StudentProfile';

interface Props {
  formFlowProps: FormFlowProps;
}

type HistoryState = {
  enrolStudent?: boolean;
  firstName: string;
  lastName: string;
  yearLevel: string;
  location?: string;
  schoolName?: string;
};

const AddStudentSpecialNeeds = ({ formFlowProps }: Props) => {
  const { jump } = formFlowProps;
  const { mutate: createStudent, isLoading: creatingStudent } = api.student.create.useMutation();
  const history = useHistory<HistoryState>();
  const enrolStudent = history.location.state?.enrolStudent;

  // useFormContext seems to be bugged, the AddStudent component is remounted when navigating to this subroute
  // so formUtils.getValues() returns empty, calling formUtils.setValue will cause another remounting of AddStudent
  const formUtils = useForm<StudentProfileData>({
    defaultValues: {
      ...history.location.state,
      specialLearningNeeds: [],
    },
    resolver: zodResolver(StudentProfileData),
  });

  const { firstName, lastName, yearLevel, location } = formUtils.getValues();
  const { refetch } = api.student.all.useQuery();
  const loading = creatingStudent || (formUtils.formState.isSubmitted && !formUtils.formState.errors.root);

  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader({
      label: 'Students',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!firstName) {
      jump(0);
    }
  }, [firstName, jump]);

  const pollStudentsApi = (attempts: number) => {
    if (attempts >= 20) {
      history.push(PATH_HUB_HOME);
    }
    setTimeout(async () => {
      const { data } = await refetch();
      const newStudent = data.find(
        (student) => student.firstName === firstName && lastName === student.lastName && yearLevel === student.yearLevel
      );
      if (!!newStudent) {
        if (enrolStudent) {
          history.push(PATH_HUB_CR_ADD_ENROLMENT, {
            firstName,
            lastName,
            yearLevel,
            location,
          });
        } else {
          formFlowProps.next({ newStudent });
        }
        return;
      }
      pollStudentsApi(attempts + 1);
    }, 5000);
  };

  const resetLearningNeeds = () => {
    formUtils.setValue('specialLearningNeeds', []);
  };

  const submit = formUtils.handleSubmit(async () => {
    formUtils.clearErrors('root');
    const { firstName, lastName, yearLevel, schoolName, specialLearningNeeds, location } = formUtils.getValues();
    const params: CreateStudentParams = {
      firstname: firstName,
      lastname: lastName,
      curriculum: location,
      yearLevel,
      schoolId: schoolName,
      specialLearningNeeds: !isEmpty(specialLearningNeeds)
        ? convertHubToSfLearningNeeds(specialLearningNeeds).join(';')
        : null,
    };
    createStudent(params, {
      onSuccess: async () => {
        pollStudentsApi(0);
      },
      onError: (error) => {
        formUtils.setError('root', {
          message: `There was an error encountered adding the student. Please try again. ${error}`,
        });
      },
    });
  });

  const skipSpecialNeeds = () => {
    resetLearningNeeds();
    submit();
  };

  return (
    <div className="md:max-w-4/5 md:w-4/5">
      <ShadowContainer className="flex flex-col gap-6 !rounded-3xl bg-white p-6 md:!p-10">
        <h1 className="font-bold md:text-2xl">Special learning needs</h1>
        {loading ? (
          <ClueyPlusSpinner />
        ) : (
          <ButtonAsLink onClick={skipSpecialNeeds} className="flex items-center gap-1.5">
            <span className="link">Skip this step</span>
            <ArrowBackIcon className="link rotate-[-180deg]" width={20} height={20} />
          </ButtonAsLink>
        )}
        <SpecialNeeds student={formUtils.getValues()} formUtils={formUtils} />
        {formUtils.formState.errors?.root && (
          <p className="mt-1 text-xs text-red-5">{formUtils.formState.errors?.root.message}</p>
        )}
        <div className="flex flex-col gap-4 pt-3 md:flex-row md:justify-start">
          <Button
            onClick={submit}
            disabled={loading}
            className="flex h-10 w-full items-center justify-center gap-3 self-start normal-case md:w-[256px]"
            size="sm"
          >
            {loading ? (
              <InlineLoader />
            ) : (
              <>
                <span>Save and continue</span>
                <ChevronDownIcon className="rotate-[-90deg] text-white" />
              </>
            )}
          </Button>
          {!loading && (
            <Button
              onClick={() => {
                history.push(PATH_HUB_HOME);
              }}
              className="flex h-10 items-center justify-center !border normal-case"
              size="sm"
              appearance="reverse"
            >
              <span className="text-base font-medium tracking-wide text-grey-400">Cancel</span>
            </Button>
          )}
        </div>
      </ShadowContainer>
    </div>
  );
};

export { AddStudentSpecialNeeds };
